// AI Assistant — floating chat copilot.
//
// A persistent button in the bottom-right corner opens a chat panel.
// The user types what they want in plain language and the backend
// (POST /api/ai/assistant) picks the best catalogue tool, extracts
// parameters and returns a `/box/<slug>?<query>` URL we navigate to.
//
// State lives entirely in this component — there is no global store
// because the conversation is intentionally short-lived (close → reset).
// The quota indicator at the top of the panel reflects the free-tier
// monthly limit on the server.
//
// Visual language matches the rest of the app: dark zinc card, cyan
// accent, chunky uppercase labels. The button uses the same Sparkles
// icon already imported by Header.jsx so the copilot is recognisable.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Send, X, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import { useI18n } from '../i18n';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { track } from '../lib/analytics';

// Localised one-tap example prompts shown when the chat is empty.
// Each entry maps to a known catalogue tool so first-time users see
// real, working examples and don't have to invent their own prompt.
const SUGGESTIONS = {
  pt: [
    'Caixa de 200×150×80 mm com encaixe finger',
    'Luminária redonda de 30 cm de altura',
    'Par de engrenagens com 30 e 8 dentes',
    'Halftone de uma foto pra cortar no laser',
  ],
  en: [
    'A 200×150×80 mm finger-joint box',
    'A round lamp 30 cm tall',
    'A pair of gears with 30 and 8 teeth',
    'Halftone pattern from a photo to laser cut',
  ],
  es: [
    'Caja de 200×150×80 mm con encaje finger',
    'Lámpara redonda de 30 cm de alto',
    'Par de engranajes con 30 y 8 dientes',
    'Halftone de una foto para cortar al láser',
  ],
};

// Tiny markdown-ish renderer: turns **bold** into <strong>. We avoid
// pulling a full markdown lib for a chat message that almost never
// needs more than emphasis on the picked tool slug.
function FormattedReply({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) {
          return <strong key={i} className="text-cyan-300">{p.slice(2, -2)}</strong>;
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

// Routes where the floating launcher should NOT appear. Inside the
// configurator (/box/*) the launcher overlaps the auto-rotate /
// preview controls in the bottom-right of the canvas, so we hide it
// there. Admin and account pages are also dense workspaces where the
// floating button would obstruct UI controls. Authentication flows
// (login, signup, forgot/reset) and the checkout return page (thanks)
// don't need the assistant either.
const HIDE_ON_PATHS = [
  '/box/',
  '/admin',
  '/account',
  '/login',
  '/signup',
  '/forgot',
  '/reset',
  '/verify-email',
  '/thanks',
];

function shouldHideAssistant(pathname) {
  if (!pathname) return false;
  return HIDE_ON_PATHS.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : (pathname === p || pathname.startsWith(p + '/')),
  );
}

export default function AIAssistant() {
  const { lang, t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const hiddenForRoute = shouldHideAssistant(location.pathname);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // [{ role, content, slug?, url?, params?, tier? }]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  // Feature gate — admin can disable the assistant from /admin. We poll
  // /api/ai/status once on mount and hide the launcher when it's off.
  const [aiStatus, setAiStatus] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // One-shot status check — drives whether the launcher renders at all.
  useEffect(() => {
    let alive = true;
    api('/api/ai/status').then((r) => {
      if (alive && r) setAiStatus(r);
    }).catch(() => {
      // Backend unreachable — assume enabled so we still show the
      // launcher; the assistant will surface its own error message
      // when the user tries to send.
      if (alive) setAiStatus({ enabled: true, configured: false, features: { assistant: true } });
    });
    return () => { alive = false; };
  }, []);

  // Fetch initial quota on first open so the badge "X / Y" is honest.
  useEffect(() => {
    if (!open || usage) return;
    let alive = true;
    api('/api/ai/usage?feature=assistant').then((r) => {
      if (alive && r?.usage) setUsage(r.usage);
    }).catch(() => { /* fail silently — quota is decorative */ });
    return () => { alive = false; };
  }, [open, usage]);

  // Auto-scroll on new message + auto-focus input when panel opens.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Esc to close — but only when no other modal/dialog is on top.
  // Auto-close the panel when the route changes — otherwise the slide-up
  // dialog can survive a navigation triggered from inside the chat (e.g.
  // the user clicks an "Open in editor" link) and end up on a route where
  // the launcher itself is hidden, leaving the dialog visually orphaned.
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function send(text) {
    const content = (text || '').trim();
    if (!content || busy) return;
    setError(null);
    const newMessages = [...messages, { role: 'user', content }];
    setMessages(newMessages);
    setInput('');
    setBusy(true);
    try {
      const wire = newMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }))
        .slice(-10);
      const res = await api('/api/ai/assistant', {
        method: 'POST',
        body: JSON.stringify({ messages: wire, lang }),
      });
      if (res?.usage) setUsage(res.usage);
      setMessages((cur) => [...cur, {
        role: 'assistant',
        content: res?.message || '',
        slug: res?.slug || null,
        url: res?.url || null,
        params: res?.params || {},
        tier: res?.tier || null,
      }]);
      track('ai_assistant_reply', {
        slug: res?.slug,
        params_count: Object.keys(res?.params || {}).length,
      });
    } catch (e) {
      // 429 = quota exceeded → friendly notice.
      if (e?.status === 429) {
        setError(t('aiQuotaExceeded'));
      } else if (e?.status === 503 || /not configured/i.test(e?.message || '')) {
        setError(t('aiNotConfigured'));
      } else {
        setError(t('aiError') || 'Error');
      }
    } finally {
      setBusy(false);
    }
  }

  function openTool(msg) {
    if (!msg?.url) return;
    track('ai_assistant_open_tool', {
      slug: msg.slug,
      has_params: Object.keys(msg.params || {}).length > 0,
    });
    setOpen(false);
    nav(msg.url);
  }

  // ─── Render ────────────────────────────────────────────────────────
  // Hide the launcher entirely when the admin has disabled the assistant
  // (master switch off OR features.assistant=false). The status check is
  // tolerant of failures: if the API is unreachable we still render so
  // the user can at least see the heuristic fallback in action.
  const assistantOn = !aiStatus
    ? false   // wait until status resolves to avoid a flash of the button
    : (aiStatus.enabled !== false && aiStatus.features?.assistant !== false);
  // Route gate — keep the launcher off dense workspaces (configurator,
  // admin, account) where its bottom-right position overlaps real
  // controls (e.g. the auto-rotate toggle in the 3D preview). Closing
  // the panel automatically when the route changes prevents a stuck
  // open dialog after navigation.
  if (hiddenForRoute) return null;
  if (!assistantOn) return null;

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          type="button"
          onClick={() => { setOpen(true); track('ai_assistant_open'); }}
          aria-label={t('aiAssistantOpen')}
          className="fixed bottom-5 right-5 z-[9000] flex items-center gap-2 px-4 h-12 rounded-full
                     bg-gradient-to-br from-cyan-400 to-cyan-600 text-zinc-950 font-chunky uppercase
                     tracking-wider text-xs shadow-lg shadow-cyan-500/40 hover:shadow-cyan-500/60
                     hover:scale-[1.03] active:scale-[0.98] transition-all"
        >
          <Sparkles size={16} className="shrink-0" aria-hidden />
          <span>{t('aiAssistantPill')}</span>
        </button>
      )}

      {/* Slide-up panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('aiAssistantTitle')}
          className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-[9000]
                     w-full sm:w-[400px] h-[80vh] sm:h-[560px] sm:max-h-[80vh]
                     bg-zinc-950 border border-zinc-800 sm:rounded-2xl shadow-2xl
                     flex flex-col overflow-hidden text-zinc-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-14 border-b border-zinc-800 bg-zinc-900/60">
            <div className="flex items-center gap-2 min-w-0">
              <span className="grid place-items-center w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 text-zinc-950 shrink-0">
                <Sparkles size={16} aria-hidden />
              </span>
              <div className="min-w-0">
                <div className="font-chunky uppercase tracking-wider text-xs text-zinc-100 truncate">
                  {t('aiAssistantTitle')}
                </div>
                {usage && (
                  <div
                    className="text-[10px] text-zinc-500 truncate"
                    title={t('aiQuotaTooltip')}
                  >
                    {usage.used} / {usage.limit} · {usage.tier}
                  </div>
                )}
              </div>
            </div>
            {/* Close — credit upsell handled globally by the
                AiUsagePill in the page Header (only renders on
                actual AI tool pages) plus the auto-quota-exceeded
                modal. No need for a per-launcher upsell button
                here — it was distracting from the chat input. */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors shrink-0"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-zinc-300 leading-relaxed">
                  {t('aiAssistantIntro')}
                </p>
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-chunky">
                    {t('aiTryThis')}
                  </div>
                  {(SUGGESTIONS[lang] || SUGGESTIONS.en).map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => send(s)}
                      className="block w-full text-left text-xs text-zinc-300 px-3 py-2 rounded-md
                                 bg-zinc-900 border border-zinc-800 hover:border-cyan-500/50
                                 hover:bg-zinc-800 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] text-sm rounded-2xl px-3.5 py-2.5 ${
                    m.role === 'user'
                      ? 'bg-cyan-500 text-zinc-950 rounded-br-md'
                      : 'bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-bl-md'
                  }`}
                >
                  <div className="leading-relaxed whitespace-pre-wrap">
                    {m.role === 'assistant' ? <FormattedReply text={m.content} /> : m.content}
                  </div>

                  {/* Action button: open the picked tool */}
                  {m.role === 'assistant' && m.url && (
                    <button
                      type="button"
                      onClick={() => openTool(m)}
                      className="mt-2.5 flex items-center justify-between w-full gap-2 px-3 py-2
                                 rounded-lg bg-zinc-950 border border-cyan-500/40 hover:border-cyan-400
                                 hover:bg-zinc-900 transition-colors text-xs font-chunky uppercase
                                 tracking-wider text-cyan-300"
                    >
                      <span className="truncate">
                        {t('aiOpenTool')}
                        {m.tier === 'pro' && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px]">
                            PRO
                          </span>
                        )}
                      </span>
                      <ArrowRight size={14} className="shrink-0" />
                    </button>
                  )}

                  {/* Detected params chip list */}
                  {m.role === 'assistant' && m.params && Object.keys(m.params).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(m.params).map(([k, v]) => (
                        <span
                          key={k}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700"
                        >
                          {k}: {String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-zinc-400 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span>{t('aiThinking')}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-zinc-800 bg-zinc-900/60 p-3">
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="flex items-end gap-2"
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                rows={1}
                placeholder={t('aiInputPlaceholder')}
                disabled={busy}
                className="flex-1 resize-none bg-zinc-950 border border-zinc-800 rounded-lg
                           px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500
                           focus:outline-none focus:border-cyan-500 disabled:opacity-60
                           min-h-[40px] max-h-[120px]"
              />
              <Button
                type="submit"
                size="icon"
                disabled={busy || !input.trim()}
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 shrink-0"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </form>
            <div className="mt-1.5 text-[10px] text-zinc-600 leading-snug">
              {t('aiDisclaimer')}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
